import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { useRef, useState } from 'react';

import { lowerCaseIncludes } from '@/utils/stringUtils';

type Props<T> = {
  value: string[];
  onChange: (value: string[]) => void;
  autocompleteList: T[];
  separators?: string | RegExp;
  validate?: (value: string) => boolean;
  emptyState?: JSX.Element | string;
  placeholder?: string;
};
type Item = {
  id: string;
  display: string;
};

export const MultiSelect = <T extends Item>(props: Props<T>) => {
  const { value, onChange, autocompleteList, placeholder } = props;

  const separators = props.separators ?? ',';
  const validate = props.validate ?? ((x) => !!x);

  const [text, setText] = useState('');
  const pasteMarkRef = useRef<boolean>(false);

  const filteredList = autocompleteList.filter(
    (item) =>
      lowerCaseIncludes(item.display, text) && !value.includes(item.display)
  );

  return (
    <Combobox
      immediate
      multiple
      value={value}
      onChange={(values) => {
        setText('');
        onChange(values);
      }}
    >
      <div
        className={
          'flex flex-row flex-wrap items-center gap-2 rounded-md border border-secondary p-2 focus-within:outline focus-within:outline-2 focus-within:outline-brand-green-500'
        }
      >
        {value?.map((item, idx) => (
          <span
            className="flex items-center gap-1 rounded-md border border-secondary px-2"
            key={idx}
          >
            <span>{item}</span>
            <button
              type="button"
              className="flex-shrink-0"
              onClick={() => {
                onChange((value ?? []).filter((_, i) => i !== idx));
              }}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </span>
        ))}

        <div className="grid flex-grow pl-0.5">
          <ComboboxInput
            placeholder={placeholder}
            className="col-[1] row-[1] w-auto border-none bg-transparent p-0 leading-none shadow-none outline-none"
            displayValue={() => text}
            onChange={(e) => {
              if (pasteMarkRef.current) {
                const newValues = e.target.value
                  .split(separators)
                  .map((i) => i.trim());
                const validValues = newValues.filter(validate);
                const leftOver = newValues.filter((v) => !validate(v));
                props.onChange([...(props.value ?? []), ...validValues]);
                e.target.value = '';
                setText(leftOver.length > 0 ? leftOver.join(',') : '');
              } else {
                setText(e.target.value);
              }

              pasteMarkRef.current = false;
            }}
            onPaste={() => {
              pasteMarkRef.current = true;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace') {
                if (
                  e.currentTarget.selectionStart ===
                    e.currentTarget.selectionEnd &&
                  e.currentTarget.selectionStart === 0
                ) {
                  props.onChange((props.value ?? []).slice(0, -1));
                }
              }
            }}
          />
        </div>
      </div>

      <ComboboxOptions
        anchor="bottom"
        className="z-[var(--joy-zIndex-modal)] w-[var(--input-width)] min-w-[8rem] overflow-hidden rounded-md border border-secondary bg-popover p-1 [--anchor-gap:1rem] empty:hidden"
      >
        {filteredList.map((member) => (
          <ComboboxOption
            key={member.id}
            value={member.display}
            className="text-md flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors data-[disabled]:pointer-events-none data-[focus]:bg-secondary data-[disabled]:opacity-50"
          >
            {member.display}
          </ComboboxOption>
        ))}
        {props.emptyState && filteredList.length === 0 && text.length > 0 && (
          <ComboboxOption
            value=""
            disabled
            className="text-md flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
          >
            {props.emptyState}
          </ComboboxOption>
        )}
      </ComboboxOptions>
    </Combobox>
  );
};
